import { useState } from "react";
import { api, errMsg } from "../api";
import { useMe } from "../session";
import { attachSeg, downloadUrl, isViewable, viewUrl, type AttachmentInfo, type OwnerType } from "./Attachments";
import { Modal } from "./Modal";
import { AlertCircleIcon, BanIcon, CheckCircleIcon, ClockIcon, PaperclipIcon, RepeatIcon } from "./Icons";

/** Situação de um lançamento: só o marcador (forma + cor, nunca só a cor) e o texto como dica/leitor de tela. */
export type Sit = "realizado" | "vencido" | "agendado" | "cancelado";
export const SIT_LABEL: Record<Sit, string> = { realizado: "Realizado (pago/recebido)", vencido: "Vencido (data prevista já passou)", agendado: "Agendado (a vencer)", cancelado: "Cancelado (fora de todos os saldos)" };
/** Texto curto da legenda (sem parênteses); o texto completo continua como dica ao passar o mouse e para leitor de tela. */
const SHORT: Record<Sit, string> = { realizado: "Realizado", vencido: "Vencido", agendado: "Agendado", cancelado: "Cancelado" };
const ICON = { realizado: CheckCircleIcon, vencido: AlertCircleIcon, agendado: ClockIcon, cancelado: BanIcon };

export function SituationMark({ sit, extra }: { sit: Sit; extra?: string }) {
  const Icon = ICON[sit];
  return <span className={`sit ${sit}`} role="img" aria-label={SIT_LABEL[sit]} title={extra ? `${SIT_LABEL[sit]} — ${extra}` : SIT_LABEL[sit]} data-situation-mark={sit}><Icon size={18} /></span>;
}

/** Legenda dos marcadores, mostrada no topo das tabelas de lançamentos. */
export function SituationLegend({ only, noClip, labels }: { only?: Sit[]; noClip?: boolean; labels?: Partial<Record<Sit, string>> }) {
  const items = (only ?? ["realizado", "vencido", "agendado", "cancelado"]) as Sit[];
  return (
    <div className="sit-legend" aria-label="Legenda das situações" data-sit-legend>
      <strong className="sit-legend-title">Legenda</strong>
      {items.map((s) => { const Icon = ICON[s]; return <span key={s} className={`sit ${s}`} title={SIT_LABEL[s]}><Icon size={15} /><em>{labels?.[s] ?? SHORT[s]}</em></span>; })}
      {!noClip && <span className="sit clip"><PaperclipIcon /><em>Com anexo</em></span>}
      <span className="sit clip" title="Parcela atual / total de parcelas"><i className="series-mark parcel">2/6</i><em>Parcela</em></span>
      <span className="sit clip" title="Fixo (recorrente) — número da ocorrência"><i className="series-mark fixa"><RepeatIcon /><b>3</b></i><em>Fixo</em></span>
    </div>
  );
}

/** Dias de atraso entre a data prevista e hoje ("YYYY-MM-DD"); 0 se não houver atraso. */
export function daysLate(planned: string, today: string): number {
  const a = Date.UTC(+planned.slice(0, 4), +planned.slice(5, 7) - 1, +planned.slice(8, 10));
  const b = Date.UTC(+today.slice(0, 4), +today.slice(5, 7) - 1, +today.slice(8, 10));
  return Math.max(0, Math.round((b - a) / 86_400_000));
}
export function LateDays({ planned, today }: { planned: string; today: string }) {
  const n = daysLate(planned, today);
  if (n < 1) return null;
  return <span className="late-days" title={`${n} ${n === 1 ? "dia" : "dias"} em atraso`} data-late-days={n}>{n} {n === 1 ? "dia" : "dias"}</span>;
}

/**
 * Clipe de anexos (aparece só quando há anexo). Clicar abre o documento para VISUALIZAR (PDF e imagens), sem baixar.
 * Com mais de um anexo, ou tipo que o navegador não exibe, abre uma lista (outros tipos só por download).
 */
export function ClipMark({ count, ownerType, ownerId }: { count: number; ownerType?: OwnerType; ownerId?: string }) {
  const { me } = useMe();
  const realm = me.user.realm;
  const [list, setList] = useState<AttachmentInfo[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  if (!count) return null;
  const label = `${count} ${count === 1 ? "anexo" : "anexos"}`;
  if (!ownerType || !ownerId) return <span className="row-mark clip" title={label} role="img" aria-label={label} data-has-attachment><PaperclipIcon /></span>;
  async function open(e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation();
    setErr(null);
    // a aba é aberta já no clique (senão o navegador bloqueia) e recebe o endereço depois de saber qual é o anexo
    const w = window.open("", "_blank");
    try {
      const items = await api<AttachmentInfo[]>("GET", `/api/${realm}/${attachSeg(ownerType!)}/${ownerId}/attachments`);
      if (items.length === 1 && isViewable(items[0]!) && w) { w.opener = null; w.location.href = viewUrl(realm, items[0]!.id); return; }
      w?.close(); setList(items);
    } catch (x) { w?.close(); setErr(errMsg(x)); setList([]); }
  }
  return (
    <>
      <button type="button" className="row-mark clip clip-btn" title={`${label} — clique para visualizar`} aria-label={`Visualizar ${label}`} onClick={(e) => void open(e)} data-has-attachment data-clip-open><PaperclipIcon /></button>
      {list && (
        <Modal title="Anexos" onClose={() => setList(null)}>
          {err && <p className="notice error">{err}</p>}
          <ul className="attach-list" data-clip-list>
            {list.map((a) => (
              <li key={a.id}>
                {isViewable(a)
                  ? <a href={viewUrl(realm, a.id)} target="_blank" rel="noopener noreferrer" data-attach-view>{a.fileName}</a>
                  : <a href={downloadUrl(realm, a.id)} target="_blank" rel="noopener noreferrer">{a.fileName}</a>}
                <span className="muted small">{isViewable(a) ? "visualizar" : "só download"}</span>
              </li>
            ))}
          </ul>
        </Modal>
      )}
    </>
  );
}

/** Parcela ("9/11") ou recorrência de série: fixa mostra ícone + número da ocorrência atual ("3"), ou "3/18" quando o número de ocorrências foi definido. */
export function SeriesMark({ kind, no, total }: { kind: "parcelada" | "fixa" | null; no: number | null; total: number | null }) {
  if (kind === "parcelada" && no && total) return <span className="series-mark parcel" title={`Parcela ${no} de ${total}`} data-installment={`${no}/${total}`}>{no}/{total}</span>;
  if (kind === "fixa") {
    const txt = no ? (total ? `${no}/${total}` : String(no)) : "";
    return <span className="series-mark fixa" title={no ? `Fixo (recorrente) — ocorrência ${no}${total ? ` de ${total}` : ""}` : "Lançamento fixo (recorrente)"} role="img" aria-label={no ? `Recorrente, ocorrência ${no}` : "Recorrente"} data-recurring data-occurrence={no ?? undefined}><RepeatIcon />{txt && <b>{txt}</b>}</span>;
  }
  return null;
}
